import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { RefreshCw, ShieldCheck, AlertTriangle, Phone, MailWarning, Video, Sparkles } from "lucide-react";
import { toast } from "sonner";

const KIND_LABEL = {
  abandoned_checkout: { label: "Abandoned checkouts", Icon: ShieldCheck, color: "bg-emerald-50 text-emerald-700" },
  inactive_warm: { label: "Inactive warm leads", Icon: AlertTriangle, color: "bg-amber-50 text-amber-700" },
  failed_payment: { label: "Failed payments", Icon: Phone, color: "bg-rose-50 text-rose-700" },
  no_response: { label: "No-response follow-ups", Icon: MailWarning, color: "bg-violet-50 text-violet-700" },
  webinar_no_buy: { label: "Webinar non-buyers", Icon: Video, color: "bg-pink-50 text-pink-700" },
};

export default function RecoveryCenter() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/recovery/center").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);

  const run = async (kind) => {
    setBusy(kind);
    try {
      const r = await api.post(`/recovery/run/${kind}`);
      toast.success(`AI recovery sequence started · ${r.data.job_id}`);
    } catch (e) {
      toast.error("Could not start recovery");
    }
    setBusy(null);
    load();
  };

  if (!d) return <div className="p-10 text-zinc-400">Loading recovery center…</div>;

  const s = d.summary;
  return (
    <div className="min-h-screen bg-[#FAFAFA] p-6 lg:p-10" data-testid="recovery-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-10 w-10 rounded-2xl bg-gradient-to-br from-emerald-400 to-teal-600 grid place-items-center">
          <RefreshCw className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Revenue Recovery Center</h1>
          <p className="text-sm text-zinc-500 mt-1">AI is recovering revenue you'd otherwise lose. Continuously.</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat label="Recovered revenue" value={`$${Math.round(s.recovered_revenue).toLocaleString()}`} tone="emerald" />
        <Stat label="Recovery rate" value={`${Math.round(s.recovery_rate * 100)}%`} tone="violet" />
        <Stat label="In progress" value={s.in_progress} tone="amber" />
        <Stat label="Open abandoned" value={s.open_abandoned_checkouts} tone="rose" />
      </div>

      <h2 className="font-display text-lg font-bold mb-3">Recovery streams</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {Object.entries(KIND_LABEL).map(([kind, meta]) => {
          const stat = d.by_kind?.[kind] || { count: 0, recovered: 0, in_progress: 0 };
          const Icon = meta.Icon;
          return (
            <div key={kind} className="rounded-3xl bg-white border border-black/5 p-6" data-testid={`recovery-stream-${kind}`}>
              <div className="flex items-center justify-between">
                <div className={`h-9 w-9 rounded-xl ${meta.color} grid place-items-center`}>
                  <Icon className="h-4 w-4" />
                </div>
                <span className="text-[11px] text-zinc-500">{stat.count} attempts</span>
              </div>
              <p className="mt-3 font-medium">{meta.label}</p>
              <p className="font-display text-2xl font-extrabold mt-1 text-zinc-900">
                ${Math.round(stat.recovered || 0).toLocaleString()}
              </p>
              <button
                disabled={busy === kind}
                onClick={() => run(kind)}
                data-testid={`recovery-run-${kind}`}
                className="mt-3 w-full text-xs font-medium px-3 py-2 rounded-full ashra-gradient-bg text-white hover:opacity-90 disabled:opacity-50"
              >
                {busy === kind ? "Starting…" : "Run AI sequence"}
              </button>
            </div>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-3xl bg-white border border-black/5 p-6">
          <h3 className="font-display text-sm font-bold mb-3">Recovery actions</h3>
          <ul className="space-y-2">
            {d.actions.slice(0, 10).map((a) => (
              <li key={a.id} className="flex items-center justify-between text-sm py-2 border-b border-black/5 last:border-none">
                <div className="min-w-0">
                  <p className="font-medium truncate">{a.lead_name} · <span className="text-zinc-500 font-normal">{KIND_LABEL[a.kind]?.label || a.kind}</span></p>
                  <p className="text-xs text-zinc-500 truncate">{a.detail}</p>
                </div>
                <div className="text-right shrink-0">
                  {a.status === "recovered" ? (
                    <span className="text-emerald-600 text-xs font-bold">+${Math.round(a.recovered_amount).toLocaleString()}</span>
                  ) : (
                    <span className="text-amber-600 text-xs font-medium">in progress</span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-3xl bg-gradient-to-br from-violet-50 to-pink-50 border border-violet-200/50 p-6">
          <h3 className="font-display text-sm font-bold mb-3 inline-flex items-center gap-1"><Sparkles className="h-4 w-4 text-violet-600" /> AI recovery actions in last 24h</h3>
          <ul className="space-y-3">
            {d.ai_actions.map((a, i) => (
              <li key={i} className="flex gap-2 text-sm text-zinc-700">
                <span className="text-violet-600">→</span> {a}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, tone }) {
  const toneCls = { emerald: "text-emerald-600", rose: "text-rose-600", violet: "text-violet-600", amber: "text-amber-600" }[tone] || "text-zinc-900";
  return (
    <div className="rounded-2xl bg-white border border-black/5 p-5">
      <p className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</p>
      <p className={`mt-1 font-display text-2xl font-extrabold ${toneCls}`}>{value}</p>
    </div>
  );
}
